import { useEffect, useState } from "react";
import {
    fetchAdminCategories,
    createCategory,
    updateCategory,
    deleteCategory,
    type AdminCategory,
} from "../api/tcg";

// Die vier Gruppen (product & community sind abstimmbar, jury & performance nicht).
const GROUPS = [
    { key: "product", label: "Product Excellence" },
    { key: "community", label: "Community & Creator" },
    { key: "jury", label: "Jury (Business Case)" },
    { key: "performance", label: "Performance" },
];

// Manuell pflegbare Sprachen. Englisch ist das Namensfeld oben.
const TRANSLATION_LANGS = [
    { key: "de" as const, flag: "🇩🇪", label: "Deutsch" },
    { key: "es" as const, flag: "🇪🇸", label: "Español" },
    { key: "pt" as const, flag: "🇵🇹", label: "Português" },
];

type LangKey = (typeof TRANSLATION_LANGS)[number]["key"];
type TranslationFields = Record<LangKey, string>;

const EMPTY_TRANSLATIONS: TranslationFields = { de: "", es: "", pt: "" };

function AdminCategories({ token }: { token: string }) {
    const [categories, setCategories] = useState<AdminCategory[]>([]);
    const [name, setName] = useState("");
    const [groupKey, setGroupKey] = useState("product");
    const [description, setDescription] = useState("");
    const [translations, setTranslations] = useState<TranslationFields>(EMPTY_TRANSLATIONS);
    // Aktuelle DeepL-Vorschläge, nur als Platzhalter – nie als Wert.
    const [autoTranslations, setAutoTranslations] = useState<TranslationFields>(EMPTY_TRANSLATIONS);
    const [editingId, setEditingId] = useState<number | null>(null);
    const [message, setMessage] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [busy, setBusy] = useState(false);

    async function load() {
        try {
            setCategories(await fetchAdminCategories(token));
        } catch (err) {
            setError(err instanceof Error ? err.message : "Konnte Kategorien nicht laden.");
        }
    }

    useEffect(() => { load(); }, []);

    function resetForm() {
        setEditingId(null);
        setName("");
        setGroupKey("product");
        setDescription("");
        setTranslations(EMPTY_TRANSLATIONS);
        setAutoTranslations(EMPTY_TRANSLATIONS);
    }

    function startEdit(cat: AdminCategory) {
        setEditingId(cat.id);
        setName(cat.name);
        setGroupKey(cat.group_key ?? "product");
        setDescription(cat.description ?? "");

        // Nur manuelle Werte ins Eingabefeld. Automatische landen im
        // Platzhalter – sonst würde jedes Speichern sie festschreiben.
        const manual = { ...EMPTY_TRANSLATIONS };
        const auto = { ...EMPTY_TRANSLATIONS };
        for (const lang of TRANSLATION_LANGS) {
            const entry = cat.translations?.[lang.key];
            if (!entry) continue;
            if (entry.is_manual) manual[lang.key] = entry.text;
            else auto[lang.key] = entry.text;
        }
        setTranslations(manual);
        setAutoTranslations(auto);

        setMessage(null);
        setError(null);
    }

    function setTranslation(lang: LangKey, value: string) {
        setTranslations((prev) => ({ ...prev, [lang]: value }));
    }

    async function handleSubmit() {
        setBusy(true); setMessage(null); setError(null);
        try {
            const input = {
                name: name.trim(),
                group_key: groupKey,
                description: description.trim() || null,
                translations: {
                    de: translations.de.trim() || null,
                    es: translations.es.trim() || null,
                    pt: translations.pt.trim() || null,
                },
            };
            if (editingId === null) {
                await createCategory(token, input);
                setMessage("Kategorie angelegt.");
            } else {
                await updateCategory(token, editingId, input);
                setMessage("Kategorie aktualisiert.");
            }
            resetForm();
            await load();
        } catch (err) {
            setError(err instanceof Error ? err.message : "Speichern fehlgeschlagen.");
        } finally { setBusy(false); }
    }

    async function handleDelete(id: number, categoryName: string) {
        const confirmed = window.confirm(
            `Kategorie „${categoryName}" wirklich löschen?\n\nDas kann nicht rückgängig gemacht werden.`
        );
        if (!confirmed) return;

        setBusy(true); setMessage(null); setError(null);
        try {
            await deleteCategory(token, id);
            setMessage("Kategorie gelöscht.");
            if (editingId === id) resetForm();
            await load();
        } catch (err) {
            setError(err instanceof Error ? err.message : "Löschen fehlgeschlagen.");
        } finally { setBusy(false); }
    }

    return (
        <section className="admin-card">
            <h2 className="admin-card-title">Kategorien verwalten</h2>

            {message && <p className="admin-success">{message}</p>}
            {error && <p className="admin-error">{error}</p>}

            {/* Formular: Anlegen / Bearbeiten */}
            <div className="admin-control-row">
                <label className="admin-label">Name (Englisch)</label>
                <input className="admin-input" type="text" value={name}
                    onChange={(e) => setName(e.target.value)} placeholder="z.B. Game Of The Year" />

                <label className="admin-label">Gruppe</label>
                <select className="admin-input" value={groupKey} onChange={(e) => setGroupKey(e.target.value)}>
                    {GROUPS.map((g) => (
                        <option key={g.key} value={g.key}>{g.label}</option>
                    ))}
                </select>

                <label className="admin-label">Beschreibung (optional – nur Detailseite, nicht OnePager)</label>
                <input className="admin-input" type="text" value={description}
                    onChange={(e) => setDescription(e.target.value)} placeholder="Kurze Beschreibung" />

                {/* Übersetzungen des Namens */}
                <h3 className="admin-subtitle" style={{ marginTop: "20px" }}>
                    Übersetzungen des Namens
                </h3>
                <p className="admin-label" style={{ opacity: 0.75, marginTop: "-6px" }}>
                    Leer lassen = automatisch via DeepL. Ausgefüllt = euer Text, DeepL fasst ihn nie wieder an.
                </p>

                {TRANSLATION_LANGS.map((lang) => (
                    <div key={lang.key} style={{ marginBottom: "10px" }}>
                        <label className="admin-label" htmlFor={`tr-${lang.key}`}>
                            {lang.flag} {lang.label}
                        </label>
                        <input
                            id={`tr-${lang.key}`}
                            className="admin-input"
                            type="text"
                            value={translations[lang.key]}
                            onChange={(e) => setTranslation(lang.key, e.target.value)}
                            placeholder={
                                autoTranslations[lang.key]
                                    ? `Automatisch: ${autoTranslations[lang.key]}`
                                    : "Wird automatisch übersetzt"
                            }
                        />
                    </div>
                ))}
            </div>
            <div className="admin-actions">
                <button className="admin-btn-primary" onClick={handleSubmit} disabled={busy || !name.trim()}>
                    {editingId === null ? "Kategorie anlegen" : "Speichern"}
                </button>
                {editingId !== null && (
                    <button className="admin-btn-secondary" onClick={resetForm} disabled={busy}>
                        Abbrechen
                    </button>
                )}
            </div>

            {/* Liste, nach Gruppe */}
            {GROUPS.map((g) => {
                const inGroup = categories.filter((c) => (c.group_key ?? "community") === g.key);
                if (inGroup.length === 0) return null;
                return (
                    <div className="admin-action-block" key={g.key}>
                        <h3 className="admin-subtitle">{g.label}</h3>
                        <table className="admin-table">
                            <thead>
                                <tr><th>Name</th><th>Übersetzungen</th><th>Beschreibung</th><th></th></tr>
                            </thead>
                            <tbody>
                                {inGroup.map((c) => (
                                    <tr key={c.id}>
                                        <td>{c.name}</td>
                                        <td style={{ whiteSpace: "nowrap" }}>
                                            {TRANSLATION_LANGS.map((lang) => {
                                                const entry = c.translations?.[lang.key];
                                                const title = entry
                                                    ? `${lang.label}: ${entry.text}${entry.is_manual ? " (manuell)" : " (DeepL)"}`
                                                    : `${lang.label}: fehlt`;
                                                return (
                                                    <span key={lang.key} title={title} style={{ marginRight: "6px" }}>
                                                        {lang.flag}
                                                        {entry?.is_manual ? "✓" : entry ? "~" : "✕"}
                                                    </span>
                                                );
                                            })}
                                        </td>
                                        <td>{c.description || "—"}</td>
                                        <td style={{ whiteSpace: "nowrap" }}>
                                            <button className="admin-btn-secondary" style={{ marginRight: "8px" }}
                                                onClick={() => startEdit(c)} disabled={busy}>
                                                Bearbeiten
                                            </button>
                                            <button className="admin-btn-secondary"
                                                onClick={() => handleDelete(c.id, c.name)} disabled={busy}>
                                                Löschen
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                );
            })}
        </section>
    );
}

export default AdminCategories;
